import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { Mail, ArrowLeft, Loader2, CheckCircle } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/password/forgot`, { email });
      setSubmitted(true);
      toast.success("Reset link sent! Check your email.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f5f7f0] to-[#e8edda] dark:from-[#1e2711] dark:to-[#222c22] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
        <div className="flex justify-center mb-6">
          <img src="/bda-logo.png" alt="BDA Technologies" className="h-12 w-auto" />
        </div>
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-[#d8e5b8] rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#3d4f22]" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Check Your Email!</h2>
            <p className="text-slate-600 mb-4">
              If that email is registered, we've sent a password reset link to
              your inbox.
            </p>
            <p className="text-sm text-slate-500 mb-6">
              The link expires in 24 hours. Check your spam folder if you don't
              see it.
            </p>
            <Button
              onClick={() => navigate("/client/login")}
              className="bg-[#3d4f22] hover:bg-[#3d4f22]"
            >
              Back to Login
            </Button>
          </CardContent>
        </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f7f0] to-[#e8edda] dark:from-[#1e2711] dark:to-[#222c22] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
      <div className="flex justify-center mb-6">
        <img src="/bda-logo.png" alt="BDA Technologies" className="h-12 w-auto" />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl text-center">
            Forgot Password?
          </CardTitle>
          <p className="text-center text-slate-600 dark:text-slate-400 text-sm">
            Enter your email and we'll send you a password reset link.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  className="pl-10"
                  data-testid="email-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#3d4f22] hover:bg-[#3d4f22]"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                "Request Password Reset"
              )}
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => navigate("/client/login")}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </Button>
          </form>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
